import React from 'react'
import { useIntl } from 'react-intl'
import { Head } from 'vike-react/Head'

import { WebsiteHelmet } from '@slite/components'

import { getSliteWebsiteConfig } from '../pages/data'
import { Footer } from './Footer/Footer'
import { NavbarSection } from './Navbar/NavbarSection'
import { useBanner } from './useBanner'
import { useDocumentTitle } from './useDocumentTitle'

export type SliteBackgroundColor =
  | 'moon-dust'
  | 'moon-dust-lighter'
  | 'moon-dust-darker'
  | 'shade-100'
  | 'saturn-orange-lighter'
  | 'pink-moon-lighter'
  | 'blue-60'
  | 'slite-orange-300'
  | 'slite-blue-300'
  | 'slite-pink-300'

type SlitePageProps = {
  children?: React.ReactNode
  canonicalUrl: string
  title?: string
  description?: string
  ogImage?: string
  ogTitle?: string
  ogDescription?: string
  disableIndexation?: boolean
  additionalMetaTags?: { content: string; name: string }[]
  background?: SliteBackgroundColor
  footerBackground?: SliteBackgroundColor
  /** Float the navbar over the page as an overlay (reserves no layout space) —
   * for pages that open on a dark hero. */
  transparentNavbar?: boolean
  /** Whether the overlay bar currently shows its transparent/inverted look.
   * The page drives this (e.g. via an IntersectionObserver on its hero); the
   * bar turns solid when false. Defaults to true. Ignored unless
   * `transparentNavbar` is set. */
  navbarInverted?: boolean
  /** Stretch the navbar across the viewport (logo left, menu centered) — for
   * full-bleed layouts like the help center. */
  fullWidthNavbar?: boolean
  /** Cap the navbar's content at this width (keeping the left-aligned menu), so
   * it lines up with a page that uses a wider-than-default frame. */
  navbarMaxWidth?: number
  /** Skip the campaign banner — for pages that are themselves campaign
   * destinations or otherwise own their full chrome. */
  hideBanner?: boolean

  header?: React.ReactNode
  footer?: React.ReactNode

  /** Custom alternate URLs per locale (for content with different slugs per language) */
  alternateHrefLangUrls?: Record<string, string>
}

export const SlitePage = ({
  canonicalUrl,
  title,
  description,
  ogImage,
  ogTitle,
  ogDescription,
  additionalMetaTags = [],
  children,
  disableIndexation,
  background,
  footerBackground,
  transparentNavbar,
  navbarInverted = true,
  fullWidthNavbar,
  navbarMaxWidth,
  hideBanner,
  header,
  footer,
  alternateHrefLangUrls,
}: SlitePageProps) => {
  const intl = useIntl()
  const { banner, navbarTopOffset } = useBanner(!hideBanner)

  const config = getSliteWebsiteConfig(intl)

  useDocumentTitle(title ?? config.defaultTitle)

  return (
    <>
      <Head>
        <WebsiteHelmet
          locale={intl.locale}
          canonicalUrl={canonicalUrl}
          title={title ?? config.defaultTitle}
          description={description ?? config.defaultDescription}
          ogImage={ogImage ?? config.defaultOGImage}
          ogTitle={ogTitle}
          ogDescription={ogDescription}
          disableIndexation={disableIndexation}
          additionalMetaTags={additionalMetaTags}
          config={config}
          alternateHrefLangUrls={alternateHrefLangUrls}
        />
      </Head>
      {banner}
      {header ?? (
        <NavbarSection
          background={background}
          topOffset={navbarTopOffset}
          transparent={transparentNavbar}
          inverted={navbarInverted}
          fullWidth={fullWidthNavbar}
          maxWidth={navbarMaxWidth}
        />
      )}
      {children}
      {footer ?? <Footer background={footerBackground ?? background} />}
    </>
  )
}
